import { useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'

import { ValidatedForm } from '@fv/client-components'
import { showApiError, useFormModelState } from '@fv/client-core'

import { FooterBar } from '@/components/FooterBar'
import { FormActions } from '@/components/forms/FormActions'
import InfoBox from '@/components/InfoBox'
import { CheckboxField } from '@/components/inputs/CheckboxField'
import { InputGroup } from '@/components/inputs/InputGroup'

import { useCreateBrand } from './api'
import { LogoUploadButton } from './LogoUploadButton'
import type { AccountBranding } from './types'

type FormValues = {
  name: string
  isDefault: boolean
}
type Props = {
  onSuccess: (brand: AccountBranding) => void
  onCancel: () => void
}
export const CreateBrandForm = ({ onSuccess, onCancel }: Props) => {
  const { register, value } = useFormModelState<FormValues>({
    initialValue: {
      name: '',
      isDefault: false,
    },
  })
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const previewUrl = useMemo(
    () => (logoFile ? URL.createObjectURL(logoFile) : undefined),
    [logoFile],
  )

  const createBrand = useCreateBrand()

  const handleSubmit = async () => {
    if (!logoFile) {
      return toast.error('Please select a logo')
    }
    try {
      const brand = await createBrand.mutateAsync({
        file: logoFile,
        ...value,
      })
      onSuccess(brand)
    } catch (error) {
      showApiError('Failed to create brand', error)
    }
  }

  return (
    <ValidatedForm
      onValidSubmit={handleSubmit}
      className="flex flex-1 flex-col overflow-hidden"
    >
      <div className="p-6 overflow-auto flex-1">
        <div className="flex items-center gap-x-4">
          <div className="flex flex-1">
            <InputGroup
              className="mb-0 flex-1"
              inputType="text"
              required
              inputProps={{
                disabled: createBrand.isPending,
                ...register('name'),
                placeholder: 'Name your brand here',
              }}
            />
            <div className="-ml-px">
              <LogoUploadButton
                label="Upload logo"
                onSelect={setLogoFile}
                loading={createBrand.isPending}
              />
            </div>
          </div>
          <CheckboxField
            label="Use as default"
            {...register('isDefault', { valueAsChecked: true })}
          />
        </div>

        <div className=" bg-fv-blue-50 border-fv-blue-200 border my-6 p-4 flex items-center ">
          <div className="border bg-white border-fv-gray-light flex items-center">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Logo preview"
                className="max-h-52 object-contain"
              />
            ) : (
              <p className="mb-0 p-4">No logo selected</p>
            )}
          </div>
        </div>

        <InfoBox>
          <p className="text-sm">
            Logos should be at least 300 pixels wide and tightly cropped to the
            edges. Max file size is 500 KB.
          </p>
          <div className="flex gap-x-4 mt-4">
            <img
              src="https://assets.freightview.com/img/upload-logo-incorrect.svg"
              alt="bad logo example"
              className="w-44"
            />

            <img
              src="https://assets.freightview.com/img/upload-logo-correct.svg"
              alt="good logo example"
              className="w-44"
            />
          </div>
        </InfoBox>
      </div>
      <FooterBar>
        <FormActions
          onCancel={onCancel}
          submitText="Create brand"
          loading={createBrand.isPending}
        />
      </FooterBar>
    </ValidatedForm>
  )
}
